/**
 * Travel distance and duration via the Google Routes API.
 *
 * The legacy Distance Matrix API cannot be enabled on a Google Cloud project
 * created after its deprecation, so the Maps JS DistanceMatrixService this app
 * used before returns nothing on our current project. The Routes API is the
 * replacement. It is a REST service rather than a Maps JS service, so these
 * helpers call it directly.
 *
 * The Routes API honours HTTP referrer restrictions, which the legacy geocode
 * REST endpoint does not. Our browser key is referrer restricted, and calls
 * from our own pages are accepted while calls from any other referrer are
 * rejected with 403. That is why this runs in the browser with the existing
 * key instead of needing a second, server side key.
 *
 * Routes returns raw numbers (metres, and a duration like "848s"). The old
 * service returned display strings. These helpers rebuild those strings so the
 * cards read the same as before.
 */

const ROUTE_MATRIX_URL =
    'https://routes.googleapis.com/distanceMatrix/v2:computeRouteMatrix';
const METRES_PER_MILE = 1609.344;
const METRES_PER_FOOT = 0.3048;
// Google switches from miles to feet below a tenth of a mile.
const FEET_CUTOFF_METRES = METRES_PER_MILE / 10;

/**
 * Read the browser key back off the Maps loader script tag.
 *
 * The key is deliberately written in exactly one place, the loader tag in
 * public/index.html. Reading it back here keeps that single source of truth
 * instead of adding a second copy in a build variable.
 *
 * @returns {string|null} the key, or null when the loader tag is not present
 */
export const getMapsApiKey = () => {
    if (typeof document === 'undefined') return null;
    const tag = document.querySelector(
        'script[src*="maps.googleapis.com/maps/api/js"]'
    );
    const match = tag?.src?.match(/[?&]key=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : null;
};

/**
 * Read the seconds out of a Routes API duration string.
 *
 * @param {string} duration - a duration such as "848s"
 * @returns {number|null} the seconds, or null when the value is unreadable
 */
export const parseDurationSeconds = (duration) => {
    if (typeof duration !== 'string') return null;
    const match = duration.match(/^(\d+(?:\.\d+)?)s$/);
    return match ? Number(match[1]) : null;
};

/**
 * Build the duration string the cards show.
 *
 * @param {number|null} seconds - the travel time in seconds
 * @returns {string|null} a string such as "14 mins", or null when unknown
 */
export const formatDurationText = (seconds) => {
    if (typeof seconds !== 'number' || Number.isNaN(seconds)) return null;

    const unit = (value, word) => `${value} ${word}${value === 1 ? '' : 's'}`;

    // Anything under a minute still reads as a minute, the way Google shows it.
    const totalMinutes = Math.max(1, Math.round(seconds / 60));

    if (totalMinutes < 60) return unit(totalMinutes, 'min');

    const totalHours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (totalHours < 24) {
        const hoursText = unit(totalHours, 'hour');
        return minutes ? `${hoursText} ${unit(minutes, 'min')}` : hoursText;
    }

    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;
    const daysText = unit(days, 'day');
    return hours ? `${daysText} ${unit(hours, 'hour')}` : daysText;
};

/**
 * Build the distance string the cards show, in imperial units.
 *
 * @param {number|null} metres - the travel distance in metres
 * @returns {string|null} a string such as "7.7 mi", or null when unknown
 */
export const formatDistanceText = (metres) => {
    if (typeof metres !== 'number' || Number.isNaN(metres)) return null;

    if (metres < FEET_CUTOFF_METRES) {
        return `${Math.round(metres / METRES_PER_FOOT)} ft`;
    }

    const miles = metres / METRES_PER_MILE;
    // Google keeps one decimal place until the number gets long. Test the
    // rounded value, because a distance just under 100 miles rounds up to
    // "100.0" and that must read as "100" like every other three digit value.
    const rounded = Math.round(miles * 10) / 10;
    return rounded < 100 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
};

/**
 * Ask the Routes API for one origin to one destination in one travel mode.
 *
 * @param {Object} origin - the start point as { lat, lng }
 * @param {Object} destination - the end point as { lat, lng }
 * @param {string} travelMode - a Routes API mode: DRIVE, TRANSIT or BICYCLE
 * @returns {Promise<Object|null>} { distanceText, durationText } or null
 * @example
 *   await getTravelEstimate({ lat: 38.6, lng: -90.3 }, docLatLong, 'DRIVE');
 */
export const getTravelEstimate = async (origin, destination, travelMode) => {
    const key = getMapsApiKey();
    if (!key) return null;

    const waypoint = (point) => ({
        waypoint: {
            location: {
                latLng: {
                    latitude: Number(point.lat),
                    longitude: Number(point.lng),
                },
            },
        },
    });

    const response = await fetch(ROUTE_MATRIX_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Goog-Api-Key': key,
            'X-Goog-FieldMask':
                'originIndex,destinationIndex,duration,distanceMeters,condition',
        },
        body: JSON.stringify({
            origins: [waypoint(origin)],
            destinations: [waypoint(destination)],
            travelMode,
        }),
    });

    if (!response.ok) return null;

    const rows = await response.json();
    const row = Array.isArray(rows) ? rows[0] : null;
    // A pair with no route between them comes back without ROUTE_EXISTS.
    if (!row || row.condition !== 'ROUTE_EXISTS') return null;

    return {
        distanceText: formatDistanceText(row.distanceMeters),
        durationText: formatDurationText(parseDurationSeconds(row.duration)),
    };
};
